import { Observable, PartialObserver, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

/**
 * Subscribes to the source observable and immediately closes after the first value is emitted from the subscription
 * @param observable source observable
 * @param observer PartialObserver object
 */
export function subscribeOnce<T>(
  observable: Observable<T>,
  observer?: PartialObserver<T>
): void;
/**
 * Subscribes to the source observable and immediately closes after the first value is emitted from the subscription
 * @param observable source observable
 * @param next callback function with value T as parameter
 * @param error callback error handler function
 * @param complete callback function after first emission from source observable
 */
export function subscribeOnce<T>(
  observable: Observable<T>,
  next?: (value: T) => void,
  error?: (error: unknown) => void,
  complete?: () => void
): void;
export function subscribeOnce<T>(
  observable: Observable<T>,
  arg1?: ((value: T) => void) | PartialObserver<T>,
  arg2?: (error: unknown) => void,
  arg3?: () => void
) {
  let subscription: null | Subscription = null;

  function unsubscribe() {
    // Unsubscribe on next turn to ensure `subscription` isn't `null` in some use cases.
    setTimeout(() => {
      subscription?.unsubscribe();
    });
  }

  const observer: PartialObserver<T> = {} as PartialObserver<T>;

  if (typeof arg1 === 'function') {
    observer.next = arg1;

    observer.error = (error: unknown) => {
      unsubscribe();
      if (typeof arg2 === 'function') {
        arg2(error);
      }
    };

    observer.complete = () => {
      unsubscribe();
      if (typeof arg3 === 'function') {
        arg3();
      }
    };
  } else {
    if (typeof arg1?.next === 'function') {
      observer.next = arg1.next;
    }

    observer.error = (error: unknown) => {
      unsubscribe();
      if (typeof arg1?.error === 'function') {
        arg1.error(error);
      }
    };

    observer.complete = () => {
      unsubscribe();
      if (typeof arg1?.complete === 'function') {
        arg1.complete();
      }
    };
  }

  subscription = observable.pipe(take(1)).subscribe(observer);
}
